/**
 * Services Modal - For selling pass-through services (Airtime, Electricity, DSTV, Vouchers)
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Smartphone, 
  Zap, 
  Tv, 
  Ticket,
  Plus,
  Info
} from "lucide-react";
import { toast } from "sonner";

const SERVICE_TYPES = [
  { 
    id: "AIRTIME", 
    name: "Airtime", 
    icon: Smartphone, 
    color: "bg-blue-500 hover:bg-blue-600",
    markup: 0  // 0% markup - pass-through
  },
  { 
    id: "ELECTRICITY", 
    name: "Electricity", 
    icon: Zap, 
    color: "bg-yellow-500 hover:bg-yellow-600",
    markup: 0  // 0% markup - pass-through
  },
  { 
    id: "DSTV", 
    name: "DSTV", 
    icon: Tv, 
    color: "bg-purple-500 hover:bg-purple-600",
    markup: 0  // 0% markup - pass-through
  },
  { 
    id: "VOUCHER", 
    name: "Voucher", 
    icon: Ticket, 
    color: "bg-green-500 hover:bg-green-600",
    markup: 5  // 5% markup
  },
];

export const ServicesModal = ({ open, onClose, onAddService }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [amount, setAmount] = useState("");

  const handleSelectType = (type) => {
    setSelectedType(type);
    setAmount("");
  };

  const calculateSellingPrice = (baseAmount, markup) => {
    const base = parseFloat(baseAmount) || 0;
    return base * (1 + markup / 100);
  };

  const handleAddToCart = () => {
    const baseAmount = parseFloat(amount);
    
    if (!baseAmount || baseAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!selectedType) {
      toast.error("Please select a service type");
      return;
    }

    const sellingPrice = calculateSellingPrice(baseAmount, selectedType.markup);
    const profit = sellingPrice - baseAmount;

    // Create a service item for the cart
    const serviceItem = {
      product_id: `SERVICE-${selectedType.id}-${Date.now()}`,
      plu_code: `SVC-${selectedType.id}`,
      name: `${selectedType.name} - R${baseAmount.toFixed(2)}`,
      quantity: 1,
      unit_price: sellingPrice,
      cost_price: baseAmount,  // Cost = base amount (what you pay provider)
      discount_percent: 0,
      max_discount_percent: 0,  // No discount allowed on services
      no_discount: true,
      sold_per_kg: false,
      total: sellingPrice,
      max_stock: 999999,  // Unlimited "stock"
      vat_category: "Zero-rated 0%",  // Services are typically zero-rated
      is_service: true,  // Flag to identify service items
      service_type: selectedType.id,
      base_amount: baseAmount,
      markup_percent: selectedType.markup,
      profit: profit
    };

    onAddService(serviceItem);
    toast.success(`Added ${selectedType.name} R${baseAmount.toFixed(2)} to cart`);
    
    // Reset for next entry
    setAmount("");
    setSelectedType(null);
  };

  const handleQuickAmount = (quickAmount) => {
    setAmount(quickAmount.toString());
  };

  const quickAmounts = [10, 20, 50, 100, 200, 500];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Ticket className="h-6 w-6 text-blue-600" />
            Services
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Service Type Selection */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Select Service Type</Label>
            <div className="grid grid-cols-2 gap-3">
              {SERVICE_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <Button
                    key={type.id}
                    onClick={() => handleSelectType(type)}
                    className={`h-20 flex flex-col items-center justify-center gap-2 ${
                      selectedType?.id === type.id 
                        ? type.color + " ring-2 ring-offset-2 ring-blue-400" 
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                    }`}
                    data-testid={`service-type-${type.id.toLowerCase()}`}
                  >
                    <Icon className="h-8 w-8" />
                    <span className="font-semibold">{type.name}</span>
                    {type.markup > 0 && (
                      <span className="text-xs opacity-80">+{type.markup}%</span>
                    )}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Amount Entry */}
          {selectedType && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Enter Amount (R)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="text-2xl h-14 text-center font-bold"
                autoFocus
                data-testid="service-amount-input"
              />

              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-6 gap-2">
                {quickAmounts.map((qa) => (
                  <Button
                    key={qa}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickAmount(qa)}
                    className="text-sm"
                  >
                    R{qa}
                  </Button>
                ))}
              </div>

              {/* Price Preview */}
              {amount && parseFloat(amount) > 0 && (
                <Card className={selectedType.markup > 0 ? "bg-green-50 border-green-200" : "bg-gray-50"}>
                  <CardContent className="py-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Base Amount:</span>
                      <span className="font-semibold">R {parseFloat(amount).toFixed(2)}</span>
                    </div>
                    {selectedType.markup > 0 && (
                      <>
                        <div className="flex justify-between items-center text-green-600">
                          <span className="text-sm">+ {selectedType.markup}% Markup:</span>
                          <span className="font-semibold">
                            R {(parseFloat(amount) * selectedType.markup / 100).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center border-t pt-2 mt-2">
                          <span className="font-bold">Customer Pays:</span>
                          <span className="font-bold text-lg text-green-700">
                            R {calculateSellingPrice(amount, selectedType.markup).toFixed(2)}
                          </span>
                        </div>
                      </>
                    )}
                    {selectedType.markup === 0 && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                        <Info className="h-3 w-3" />
                        <span>Pass-through - No profit on this service</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Add to Cart Button */}
          <Button
            onClick={handleAddToCart}
            disabled={!selectedType || !amount || parseFloat(amount) <= 0}
            className="w-full h-12 text-lg bg-blue-600 hover:bg-blue-700"
            data-testid="add-service-to-cart"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add to Cart
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServicesModal;
